/**
 * Holland & Knight Design Tokens
 * Based on HKBrandGuidelines.pdf and docs/index.html style guide
 */

// ============================================
// SPACING - Official HK spacing scale
// ============================================
export const spacing = {
  xs: 8,    // 0.5rem
  sm: 16,   // 1rem
  md: 24,   // 1.5rem
  lg: 32,   // 2rem
  xl: 48,   // 3rem
  '2xl': 64,  // 4rem
} as const;

// ============================================
// BORDER RADIUS
// ============================================
export const borderRadius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 16,
  xl: 24,
  full: 999,
} as const;

// ============================================
// SHADOWS - HK brand shadows with primary blue tint
// ============================================
export const shadows = {
  sm: '0 1px 2px 0 rgba(1, 33, 105, 0.05)',
  md: '0 4px 6px -1px rgba(1, 33, 105, 0.1)',
  lg: '0 10px 15px -3px rgba(1, 33, 105, 0.1)',
  xl: '0 20px 25px -5px rgba(1, 33, 105, 0.1)',
  '2xl': '0 25px 50px -12px rgba(1, 33, 105, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(1, 33, 105, 0.06)',
  none: 'none',
} as const;

// ============================================
// TYPOGRAPHY - App font stacks
// ============================================
export const fontFamilies = {
  // Primary font for marketing and UI
  marketing: 'Montserrat, "Helvetica Neue", Arial, system-ui, sans-serif',
  // Body text (same as marketing)
  body: 'Montserrat, "Helvetica Neue", Arial, system-ui, sans-serif',
  // Written correspondence (legal documents)
  correspondence: '"Times New Roman", "Cambria", Georgia, serif',
  // Presentations
  presentation: 'Arial, "Helvetica Neue", sans-serif',
  // Display/headlines (optional premium use)
  display: '"Goudy Old Style", Garamond, Georgia, serif',
  // Monospace (code, technical) - Roboto Mono loaded via Google Fonts
  mono: '"Roboto Mono", "SF Mono", Monaco, Inconsolata, monospace',
} as const;

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// Font sizes in pixels - matching hk-bedrock-chat tailwind scale
export const fontSizes = {
  xs: 12,        // 0.75rem - hk-xs (captions, labels)
  sm: 14,        // 0.875rem - hk-sm (body small, nav items)
  base: 16,      // 1rem - hk-base (body text)
  lg: 18,        // 1.125rem - hk-lg (body large)
  xl: 20,        // 1.25rem - hk-xl (h5)
  '2xl': 24,     // 1.5rem - hk-2xl (h4)
  '3xl': 30,     // 1.875rem - hk-3xl (h3)  
  '4xl': 36,     // 2.25rem - hk-4xl (h2)
  '5xl': 48,     // 3rem - hk-5xl (h1, display)
  // Legacy aliases for backwards compatibility
  display: 48,
  h1: 40,
  h2: 32,
  h3: 24,
  h4: 20,
  bodyLarge: 18,
  body: 16,
  bodySmall: 14,
  caption: 12,
  overline: 11,  // Slightly larger for readability
} as const;

export const lineHeights = {
  tight: 1.2,       // Headings
  snug: 1.375,      // Large text
  normal: 1.5,      // Body text
  relaxed: 1.6,     // Reading-optimized
  loose: 1.75,      // Spacious
} as const;

// ============================================
// ANIMATION DURATIONS
// ============================================
export const durations = {
  fast: 100,
  normal: 200,
  slow: 300,
  slower: 500,
} as const;

// ============================================
// Z-INDEX SCALE
// ============================================
export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const;

// ============================================
// BUTTON SPECIFICATIONS
// ============================================
export const buttonStyles = {
  // Base button
  base: {
    padding: '12px 24px',
    borderRadius: borderRadius.md,
    fontWeight: fontWeights.semibold,
    fontSize: fontSizes.bodySmall,
    borderWidth: 2,
    transition: 'all 0.2s ease',
  },
  // Size variants
  sizes: {
    small: {
      padding: '8px 16px',
      fontSize: fontSizes.caption,
    },
    medium: {
      padding: '12px 24px',
      fontSize: fontSizes.bodySmall,
    },
    large: {
      padding: '16px 32px',
      fontSize: fontSizes.body,
    },
  },
  // Focus outline
  focus: {
    outline: '2px solid #00A9E0',
    outlineOffset: 2,
  },
  // Disabled state
  disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
} as const;

// ============================================
// FORM INPUT SPECIFICATIONS
// ============================================
export const inputStyles = {
  borderWidth: 1.5,
  borderRadius: borderRadius.md,
  padding: '12px 16px',
  fontSize: fontSizes.body,
} as const;

// ============================================
// CARD SPECIFICATIONS
// ============================================
export const cardStyles = {
  borderRadius: borderRadius.md,
  padding: spacing.md,
  shadow: shadows.md,
  hoverShadow: shadows.lg,
} as const;

const tokens = {
  spacing,
  borderRadius,
  shadows,
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  durations,
  zIndex,
  buttonStyles,
  inputStyles,
  cardStyles,
};

export default tokens;
